import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { DateTime } from 'luxon';

import { API } from 'constants';
import client from '../../../services/organization-api';

import { USER_ROLE } from '../../../constants/organization';

import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useFetchDataOnLoad } from '../../../hooks/useFetchDataOnLoad';
import { useManageSearchParams } from '../../../hooks/useManageSearchParams';
import { useDefaultSearchParams } from '../../../hooks/useDefaultSearchParams';
import { useHandlePageDataLoadError } from '../../../hooks/useHandlePageDataLoadError';

import Loading from '../../../components/Loading';
import TableV2 from '../../../design-system/TableV2/TableV2';
import { Button } from '../../../design-system';
import { ArrowGoBackLineIcon } from '../../../design-system/Icons';

const LIMIT = 20;
const SETTINGS_ROUTE = '/settings';

const ActivityLogPage = () => {
    const { organizationId } = useParams();
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    const page = Number(searchParams.get('page')) || 1;

    const [tableData, setTableData] = useState(null);

    const { handlePageDataLoadError } = useHandlePageDataLoadError();
    const { setSearchParam } = useManageSearchParams();

    const { data: organizationData } = useFetchDataOnLoad({
        client,
        route: API.ROUTES.organization.organization + organizationId + '/',
        formatResponse: ({ organization_data }) => organization_data,
        onError: (e) =>
            handlePageDataLoadError({
                e,
                redirectPath: SETTINGS_ROUTE,
                generalErrorHandler: () => navigate(SETTINGS_ROUTE),
            }),
    });

    useDefaultSearchParams();

    useEffect(() => {
        const fetchTableData = async () => {
            try {
                if (tableData?.[page]) return;

                const { data } = await client.get(
                    API.ROUTES.organization.organization +
                        organizationId +
                        `/activity/?limit=${LIMIT}&offset=${(page - 1) * LIMIT}`
                );
                const { results, count: total } = data;

                const formattedTableData = transformActivityLogDataForTable({
                    results,
                    total,
                    page,
                    limit: LIMIT,
                });
                setTableData((prevData) => ({ ...prevData, [page]: formattedTableData }));
            } catch (e) {
                handlePageDataLoadError({
                    e,
                    redirectPath: SETTINGS_ROUTE,
                    generalErrorHandler: () => navigate(SETTINGS_ROUTE),
                });
            }
        };

        void fetchTableData();
    }, [page]);

    useDocumentTitle('Activity Log');

    // if not admin or owner -> redirect
    const memberRole = organizationData?.organization_member_role;
    if (organizationData && memberRole !== USER_ROLE.admin && memberRole !== USER_ROLE.owner) {
        return <Navigate to={SETTINGS_ROUTE} />;
    }

    if (!organizationData || !tableData) {
        return (
            <div className="page-position flex items-center justify-center w-full bg-white">
                <Loading />
            </div>
        );
    }

    const parentId = organizationData.organization?.parent;
    const backlinkHref = !!parentId
        ? `/settings/organization/${parentId}?client=${organizationId}`
        : `/settings/organization/${organizationId}`;

    return (
        <div className="page-position bg-white overflow-y-auto">
            <div className="px-5 md:px-6 py-5">
                <Button
                    type="link"
                    size="xs"
                    text={`Back to ${organizationData.organization?.name}`}
                    leadingIcon={ArrowGoBackLineIcon}
                    onClick={() => navigate(backlinkHref)}
                />
                <h1 className="font-heading-bold text-heading-bold-s text-black">Activity Log</h1>
            </div>

            {tableData[page] && (
                <TableV2
                    data={tableData[page]}
                    includePagination
                    onPaginationPreviousClick={() => setSearchParam('page', page - 1)}
                    onPaginationNextClick={() => setSearchParam('page', page + 1)}
                />
            )}

            {!tableData[page] && (
                <div className="flex items-center justify-center w-full pt-5">
                    <Loading />
                </div>
            )}
        </div>
    );
};

function transformActivityLogDataForTable({ results, page, total, limit }) {
    const result = {
        columns: [
            {
                header: ' ',
                accessor: 'index',
                type: 'text',
            },
            {
                header: 'Timestamp',
                accessor: 'timestamp',
                dividerAfter: true,
                type: 'text',
                minWidth: 220,
            },
            {
                header: 'Activity Type',
                accessor: 'type',
                type: 'text',
                minWidth: 240,
            },
            {
                header: 'User',
                accessor: 'user',
                type: 'text',
                minWidth: 260,
            },
            {
                header: 'Activity ID',
                accessor: 'activityId',
                type: 'text',
                minWidth: 330,
            },
        ],
        rows: [],
        pagination: { page, limit, totalItems: total },
    };

    const startIndex = (page - 1) * LIMIT + 1;

    results.map((item, index) => {
        const { created_at, activity_display, created_by_email, activity_id } = item;
        const timestamp = DateTime.fromISO(created_at).toFormat('MM/dd/yyyy @ h:mm a');

        const row = {
            id: activity_id,
            index: startIndex + index,
            timestamp,
            type: activity_display,
            user: created_by_email,
            activityId: activity_id,
        };

        result.rows.push(row);
    });

    return result;
}

export default ActivityLogPage;
